/**
 * Eje temporal del Gantt como dato.
 *
 * El Gantt de Planificación Avanzada se dibuja igual en las tres vistas: una
 * rejilla de columnas de ancho proporcional, una línea de "hoy" y barras
 * posicionadas en % sobre el rango visible.  Lo único que cambia entre semanal,
 * mensual y anual es *cómo se calculan* esas columnas y esas posiciones.
 *
 * Aquí se encapsula esa diferencia en un `TimeTrack`, de forma que la página y
 * las filas del Gantt no sepan en qué modo están.  Añadir un cuarto rango
 * (trimestral, plurianual…) es escribir un builder más.
 */
import type { PlanVista } from '@/components/planning/ViewModeSelect'
import {
  getWeekDays, ganttPos, todayLineLeft, getISOWeek,
  getMonthBounds, getMonthWeekSpans,
  getYearBounds, getYearMonthSpans,
  ganttPosRango, todayLineLeftRango, boundaryPcts,
} from '@/utils/planningDateRanges'

/** Una columna de la cabecera del eje. */
export interface TrackColumn {
  /** Peso en unidades `fr`: días que abarca la columna. */
  weight: number
  /** Línea superior: 'lun' | 'S12' | 'ene'. */
  primary: string
  /** Línea inferior: '3 ago' | '1 ago – 7 ago' | undefined si no aplica. */
  secondary?: string
  /** Resaltar la columna: es hoy / la semana actual / el mes actual. */
  highlight: boolean
}

export interface TimeTrack<V extends string = PlanVista> {
  vista: V
  /** En qué unidad está el eje. Es REQUERIDO a propósito: un eje nuevo tiene
   *  que declararlo, y con un `?` alguien se lo olvidaría y el fallo saldría en
   *  la geometría, que es donde más cuesta verlo. */
  granularidad: 'dia' | 'hora'
  /** Primer día del rango visible. */
  rangeStart: Date
  /** Último día del rango visible, inclusive. */
  rangeEnd: Date
  columns: TrackColumn[]
  /** Líneas de rejilla internas, en % (una menos que columnas). */
  boundaries: number[]
  /** Posición de una barra en % del carril, o null si cae fuera del rango. */
  pos: (start: Date | null, end: Date | null) => { left: number; width: number } | null
  /** Posición de la línea de "hoy" en %, o null si hoy cae fuera del rango. */
  todayX: number | null
  /** Etiqueta del rango: botón de navegación y subtítulo de la cabecera. */
  label: string
  /** Ancho mínimo en % para que quepa el texto del avance dentro de la barra. */
  minLabelPct: number
  /** Sufijo del nombre del fichero Excel: '2026-08-04' | '2026-08' | '2026'. */
  exportStamp: string
  /** Realza la segunda línea de la cabecera. En la vista semanal esa línea es el
   *  número de día, que va destacado; en la mensual es un rango de fechas y va
   *  discreto. Se conserva tal cual estaba en cada vista. */
  secondaryStrong: boolean
}

/** Traducciones y locale que necesitan los builders para las etiquetas. */
export interface TrackI18n {
  locale: string
  t: (key: string, opts?: Record<string, unknown>) => string
}

export const stripDot = (s: string) => s.replace(/\.$/, '')
export const pad2 = (n: number) => String(n).padStart(2, '0')

// ── Semanal: 7 columnas de día ────────────────────────────────────────────────
export function buildWeekTrack(offset: number, { locale, t }: TrackI18n): TimeTrack {
  const days      = getWeekDays(offset)
  const weekStart = days[0]
  const weekEnd   = days[6]
  const isoWeek   = getISOWeek(weekStart)
  const todayStr  = new Date().toDateString()

  const columns: TrackColumn[] = days.map(day => ({
    weight: 1,
    primary: stripDot(day.toLocaleDateString(locale, { weekday: 'short' })),
    secondary: stripDot(day.toLocaleDateString(locale, { day: 'numeric', month: 'short' })),
    highlight: day.toDateString() === todayStr,
  }))

  const label = offset === 0
    ? t('planning.currentWeek', { week: isoWeek })
    : t('planning.weekRange', {
        week: isoWeek,
        from: stripDot(weekStart.toLocaleDateString(locale, { day: 'numeric', month: 'short' })),
        to:   stripDot(weekEnd.toLocaleDateString(locale, { day: 'numeric', month: 'short' })),
      })

  return {
    vista: 'semanal',
    granularidad: 'dia',
    rangeStart: weekStart,
    rangeEnd: weekEnd,
    columns,
    // La semana son 7 columnas iguales: 6 líneas de rejilla equiespaciadas.
    boundaries: Array.from({ length: 6 }, (_, i) => ((i + 1) / 7) * 100),
    // Semántica exclusiva de `end`, preservada a propósito. Ver ganttPos().
    pos: (start, end) => ganttPos(start, end, weekStart),
    todayX: todayLineLeft(weekStart),
    label,
    minLabelPct: 10,
    exportStamp: `S${isoWeek}_${weekStart.getFullYear()}-${pad2(weekStart.getMonth() + 1)}-${pad2(weekStart.getDate())}`,
    secondaryStrong: true,
  }
}

// ── Mensual: columnas de semana ISO, ancho = días dentro del mes ──────────────
export function buildMonthTrack(offset: number, { locale, t }: TrackI18n): TimeTrack {
  const { monthStart, monthEnd } = getMonthBounds(offset)
  const spans     = getMonthWeekSpans(monthStart, monthEnd)
  const totalDays = monthEnd.getDate()
  const today     = new Date(); today.setHours(0, 0, 0, 0)

  const fmtDay = (d: Date) => stripDot(d.toLocaleDateString(locale, { day: 'numeric', month: 'short' }))

  const columns: TrackColumn[] = spans.map(s => ({
    weight: s.days,
    primary: `S${s.isoWeek}`,
    secondary: `${fmtDay(s.clampedStart)} – ${fmtDay(s.clampedEnd)}`,
    highlight: today >= s.start && today <= s.end,
  }))

  const monthName = monthStart.toLocaleDateString(locale, { month: 'long' })
  const year      = monthStart.getFullYear()

  return {
    vista: 'mensual',
    granularidad: 'dia',
    rangeStart: monthStart,
    rangeEnd: monthEnd,
    columns,
    boundaries: boundaryPcts(spans, totalDays),
    pos: (start, end) => ganttPosRango(start, end, monthStart, monthEnd),
    todayX: todayLineLeftRango(monthStart, monthEnd),
    label: offset === 0
      ? t('planning.currentMonth', { month: monthName, year })
      : t('planning.pastMonth',    { month: monthName, year }),
    minLabelPct: 5,
    exportStamp: `${year}-${pad2(monthStart.getMonth() + 1)}`,
    secondaryStrong: false,
  }
}

// ── Anual: 12 columnas de mes, ancho = días del mes ──────────────────────────
export function buildYearTrack(offset: number, { locale, t }: TrackI18n): TimeTrack {
  const { yearStart, yearEnd } = getYearBounds(offset)
  const spans     = getYearMonthSpans(yearStart, yearEnd)
  const totalDays = spans.reduce((sum, s) => sum + s.days, 0)   // 365 o 366
  const today     = new Date(); today.setHours(0, 0, 0, 0)
  const year      = yearStart.getFullYear()

  const columns: TrackColumn[] = spans.map(s => ({
    weight: s.days,
    primary: stripDot(s.start.toLocaleDateString(locale, { month: 'short' })),
    // El año ya sale en la etiqueta del rango; repetirlo en 12 columnas es ruido.
    secondary: undefined,
    highlight: today >= s.start && today <= s.end,
  }))

  return {
    vista: 'anual',
    granularidad: 'dia',
    rangeStart: yearStart,
    rangeEnd: yearEnd,
    columns,
    boundaries: boundaryPcts(spans, totalDays),
    pos: (start, end) => ganttPosRango(start, end, yearStart, yearEnd),
    todayX: todayLineLeftRango(yearStart, yearEnd),
    label: offset === 0
      ? t('planning.currentYear', { year })
      : t('planning.pastYear',    { year }),
    // A escala anual una OT de pocos días ocupa <1%: sólo se rotula lo ancho.
    minLabelPct: 3,
    exportStamp: String(year),
    // Irrelevante: las columnas anuales no tienen segunda línea.
    secondaryStrong: false,
  }
}

const BUILDERS: Record<PlanVista, (offset: number, i18n: TrackI18n) => TimeTrack> = {
  semanal: buildWeekTrack,
  mensual: buildMonthTrack,
  anual:   buildYearTrack,
}

export function buildTimeTrack(vista: PlanVista, offset: number, i18n: TrackI18n): TimeTrack {
  return BUILDERS[vista](offset, i18n)
}
